import { effectSlot, shouldShowMajorTickValues, type FlagField, type FlowEffect, type SensorVisibilityMode } from '@agavi/blocks';

import { getBlockLabel } from './flow-utils';

import type { Block } from '@/types/experience';

/**
 * What a Change can actually change, per block.
 *
 * The catalogue is derived from each block's CURRENT setting rather than listed flat, because the
 * useful direction is never fixed: a block already ignoring case has nothing to gain from "ignore
 * case", and offering it would put a row in the menu that applies and does nothing. So every entry
 * here is the flip of what the block holds right now, and a setting can only ever appear once.
 *
 * Two rules bound what may be added. An effect may change how strictly the FORM of an answer is
 * judged, or what the student can SEE, and never which answer is right (`caseSensitive` yes,
 * `unitMode` no). And an effect must be SUFFICIENT on its own to change the outcome, which is what
 * `mathErrorEnabled` failed: the flag fires nothing without per-category toggles and an authored
 * equation per answer, so it could apply to no visible effect at all.
 *
 * Lives in lib rather than beside the card so the catalogue can be tested without rendering one.
 */

/**
 * The flags each block type offers, in the order the menu lists them.
 *
 * A grading requirement the teacher TURNED ON is not Flow's to waive. If someone switched on
 * significant figures, precision is part of what the task is teaching, and quietly accepting
 * `23.4567` for `23.5` after a few wrong tries does not go easy on the student -- it stops
 * assessing the thing the block exists to assess, and says so to nobody. The same holds for
 * scientific notation, and for case sensitivity, which in chemistry is the difference between
 * `Pb` and `pb`. A block cannot tell a chemistry task from a spelling task, so it must not guess.
 *
 * The line that separates them is the DEFAULT, because that is the only record of intent the
 * data holds: Flow may undo a default, never a decision. All three of those are off until
 * somebody turns them on, so their presence IS the decision -- they are not offered at all.
 * `accentSensitive` ships on, so relaxing it undoes a default nobody chose, and it stays.
 *
 * Which leaves the slider's four, and they are not grading at all -- they only reveal more of the
 * control. `invariants.test.ts` pins every default here against the block that owns it, so this
 * reasoning cannot quietly go stale.
 */
const FLAGS_BY_TYPE: Partial<Record<Block['type'], FlagField[]>> = {
  keyword: ['accentSensitive'],
  fitb: ['accentSensitive'],
  slider: ['showTickMarks', 'showMajorTickValues', 'showLiveReadout', 'showLabels'],
};

/**
 * What a block holds when its data says nothing, per field.
 *
 * Duplicated from each block's own `?? ` fallback, and it has to match: the catalogue offers the
 * OPPOSITE of the current value, so reading an unset field as the wrong default offers the flip
 * that does nothing. A test pins each one against the block that owns it.
 */
const FLAG_DEFAULTS: Record<FlagField, boolean> = {
  caseSensitive: false,
  // Accents default ON while case defaults OFF, which is the pair most likely to be assumed
  // symmetric and is not. `invariants.test.ts` pins both against the blocks that own them.
  accentSensitive: true,
  sigFigsRequired: false,
  requireExponentialNotation: false,
  showTickMarks: false,
  showMajorTickValues: false,
  showLiveReadout: true,
  showLabels: true,
};

/**
 * What Numerical compares against when the teacher has never opened the sig-figs field.
 *
 * A third copy of a block default, here for the same reason as `FLAG_DEFAULTS`: the row has to
 * name the count, and an unset field still has one. Getting it wrong is worse than the flags,
 * because the row would name a number the block does not enforce. Pinned in `invariants.test.ts`.
 */
const SIG_FIGS_DEFAULT = 3;

/**
 * The value of each flag that OPENS something for the student.
 *
 * Every effect opens; none closes. That is the whole rule, and it is structural rather than
 * advisory -- `resolveChanges` returns every matching change's effects in list order with no
 * dedupe and no conflict check, and a card's exclusivity only ever covered its own rows. So two
 * changes could hold the same setting pointing opposite ways, and whichever sat later in the list
 * won, silently.
 *
 * Worse, it could not settle. Effects latch, and an attempts condition never goes false once true,
 * so both changes went on matching every submission afterwards and the "latch" was really a
 * tug-of-war re-run each time. A drill loop built that way -- relax after two tries, tighten once
 * correct -- dropped back to lenient the moment the student got one wrong, which is the opposite
 * of a drill.
 *
 * Restricting the catalogue to the opening direction removes the whole class by construction: two
 * effects touching one setting can now only agree, so order stops mattering and a teacher cannot
 * author a contradiction. The closing direction is not merely unsafe, it never worked.
 *
 * Which direction opens is per field, not uniform: the matching flags open by being OFF (ignore
 * accents), the slider's display flags open by being ON (show the tick values).
 */
const OPEN_VALUE: Record<FlagField, boolean> = {
  // Kept total over `FlagField` even for the three the menu no longer offers: a change authored
  // before they were withdrawn can still hold one, and it has to keep describing itself.
  caseSensitive: false,
  accentSensitive: false,
  sigFigsRequired: false,
  requireExponentialNotation: false,
  // Show the student more while they drag. Scaffolds, so the open direction is ON.
  showTickMarks: true,
  showMajorTickValues: true,
  showLiveReadout: true,
  showLabels: true,
};

/**
 * What KIND of help an effect is, for the glyph and colour the menu puts in front of it.
 *
 * Grouped by what the effect does for the student, not by its `kind` -- `setFlag` covers both
 * "ignore accents" and "show the tick values", which are different offers and should not wear the
 * same mark. Three families, so a row is identifiable before it is read, and so the options stop
 * looking like more block names.
 */
export type EffectTone =
  /** Judge an answer less strictly. */
  | 'relax'
  /** Show the student more of the control. */
  | 'reveal'
  /** Open a route out of an attempt the device made impossible. */
  | 'rescue';

const FLAG_TONE: Record<FlagField, EffectTone> = {
  caseSensitive: 'relax',
  accentSensitive: 'relax',
  sigFigsRequired: 'relax',
  requireExponentialNotation: 'relax',
  showTickMarks: 'reveal',
  showMajorTickValues: 'reveal',
  showLiveReadout: 'reveal',
  showLabels: 'reveal',
};

export function effectTone(effect: FlowEffect): EffectTone {
  if (effect.kind === 'unlockBypass') return 'rescue';
  if (effect.kind === 'setVisibility') return 'reveal';
  return FLAG_TONE[effect.field];
}

/**
 * How much of itself a sensor shows: `passive` hides the control, `capture` gives a button,
 * `live` gives a continuous reading. Only moves UP this ladder are offered, which is the same
 * monotonic rule the flags follow -- each step reveals strictly more than the one below it.
 */
const VISIBILITY_RANK: Record<SensorVisibilityMode, number> = { passive: 0, capture: 1, live: 2 };

/** GPS opens on Capture and Compass on Live, so the two offer different rows out of the box. */
const VISIBILITY_DEFAULTS: Partial<Record<Block['type'], SensorVisibilityMode>> = {
  gps: 'live',
  compass: 'live',
};

const VISIBILITY_MODES: SensorVisibilityMode[] = ['live', 'capture', 'passive'];

/**
 * The block panels' own keys for the three modes, reused rather than restated.
 *
 * `passive` is the stored value; "Capture on submit" is what the teacher has always been shown for
 * it. A Flow-side copy would have had to know that, and would have drifted the first time the
 * wording changed on the block.
 */
const CAPTURE_MODE_KEYS: Record<SensorVisibilityMode, string> = {
  live: 'blocks.captureMode.live',
  capture: 'blocks.captureMode.capture',
  passive: 'blocks.captureMode.onSubmit',
};

/** Blocks whose manual entry `unlockBypass` can open. Mirrors `bypassBlocks` in flow-utils. */
const BYPASS_TYPES = new Set<Block['type']>(['rotation', 'compass']);

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export interface EffectOption {
  /** Stable, and round-trips through `parseEffectKey` — the picker hands back only this. */
  key: string;
  effect: FlowEffect;
  /**
   * Short form, for a menu row already sitting under its block's header.
   *
   * Named `text` rather than `label` because the picker filters on exactly this field, and a
   * catalogue entry that has to be renamed on the way into the menu is one more place for the two
   * to drift apart.
   */
  text: string;
  /** Why it cannot be chosen, if it cannot. Greyed with the reason, never hidden. */
  disabled?: string;
}

export interface EffectGroup {
  blockId: string;
  blockLabel: string;
  options: EffectOption[];
}

function flagValue(block: Block, field: FlagField): boolean {
  const raw = block.data[field];
  return typeof raw === 'boolean' ? raw : FLAG_DEFAULTS[field];
}

function visibilityValue(block: Block): SensorVisibilityMode {
  const raw = block.data.visibilityMode;
  return VISIBILITY_MODES.includes(raw as SensorVisibilityMode)
    ? raw as SensorVisibilityMode
    : VISIBILITY_DEFAULTS[block.type] ?? 'capture';
}

function num(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

/** The i18n key for a flag's flip, e.g. `...action.caseSensitive.off`. */
function flagActionKey(field: FlagField, value: boolean): string {
  return `task.flow.changes.action.${field}.${value ? 'on' : 'off'}`;
}

/**
 * What a flip is CALLED, in the block's own words.
 *
 * Every string here mirrors the label on the block's own options bar, because a change that
 * renames the thing it changes makes a teacher match two vocabularies to find one setting. The
 * three sensor rows go further and read `blocks.captureMode.*` directly — the same keys the GPS
 * and Compass panels render — rather than keeping a Flow-side copy of "Capture on submit".
 *
 * Significant figures take the count as well. The flag alone is sufficient (`sigFigsCount`
 * defaults to 3, so the comparison always bites), but silently enforcing a number the change never
 * names is its own kind of insufficient. Read from the block at render time, never baked in, so
 * the row still tells the truth after the teacher edits the count.
 */
function flagActionText(block: Block, field: FlagField, value: boolean, t: Translate): string {
  if (field === 'sigFigsRequired' && value) {
    const raw = num(block.data.sigFigsCount);
    const count = Number.isFinite(raw) ? raw : SIG_FIGS_DEFAULT;
    return t('task.flow.changes.action.sigFigsRequired.onCount', { count });
  }
  return t(flagActionKey(field, value));
}

export function effectKey(effect: FlowEffect): string {
  if (effect.kind === 'setFlag') return `setFlag:${effect.blockId}:${effect.field}:${effect.value ? '1' : '0'}`;
  if (effect.kind === 'setVisibility') return `setVisibility:${effect.blockId}:${effect.mode}`;
  return `unlockBypass:${effect.blockId}`;
}

/**
 * The inverse of `effectKey`, for a picker that hands back nothing but the key.
 *
 * Total rather than throwing: a key can arrive from a menu built against blocks that have since
 * changed, and a change that silently adds nothing beats one that takes the editor down.
 */
export function parseEffectKey(key: string): FlowEffect | null {
  const parts = key.split(':');
  if (parts[0] === 'unlockBypass' && parts.length === 2 && parts[1]) {
    return { kind: 'unlockBypass', blockId: parts[1] };
  }
  if (parts[0] === 'setVisibility' && parts.length === 3 && parts[1]
      && VISIBILITY_MODES.includes(parts[2] as SensorVisibilityMode)) {
    return { kind: 'setVisibility', blockId: parts[1], mode: parts[2] as SensorVisibilityMode };
  }
  if (parts[0] === 'setFlag' && parts.length === 4 && parts[1]
      && Object.prototype.hasOwnProperty.call(FLAG_DEFAULTS, parts[2] ?? '')
      && (parts[3] === '0' || parts[3] === '1')) {
    return { kind: 'setFlag', blockId: parts[1], field: parts[2] as FlagField, value: parts[3] === '1' };
  }
  return null;
}

/**
 * Why a chosen flip would still show the student nothing.
 *
 * Only the slider's tick VALUES have one: past a readable number of major ticks the labels are
 * suppressed whatever the toggle says, so turning them on there is the "applies and does nothing"
 * case the catalogue's second rule exists to refuse. Asked of the same function the renderer asks.
 */
function inertReason(block: Block, effect: FlowEffect, t: Translate): string | undefined {
  if (effect.kind !== 'setFlag' || effect.field !== 'showMajorTickValues' || !effect.value) return undefined;
  const dense = !shouldShowMajorTickValues(
    true,
    block.data.scaleMode === 'log' ? 'log' : 'linear',
    num(block.data.min), num(block.data.max), num(block.data.majorTickInterval),
  );
  return dense ? t('task.flow.changes.tickValuesTooDense') : undefined;
}

/** Every effect this one block offers, given what it currently holds. */
export function effectOptionsFor(block: Block, t: Translate): EffectOption[] {
  const options: EffectOption[] = [];

  for (const field of FLAGS_BY_TYPE[block.type] ?? []) {
    const value = OPEN_VALUE[field];
    // Already open: there is nothing left to offer, and the closing flip is not on the menu.
    if (flagValue(block, field) === value) continue;
    const effect: FlowEffect = { kind: 'setFlag', blockId: block.id, field, value };
    options.push({
      key: effectKey(effect),
      effect,
      text: flagActionText(block, field, value, t),
      disabled: inertReason(block, effect, t),
    });
  }

  if (block.type in VISIBILITY_DEFAULTS) {
    const current = visibilityValue(block);
    for (const mode of VISIBILITY_MODES) {
      if (VISIBILITY_RANK[mode] <= VISIBILITY_RANK[current]) continue;
      const effect: FlowEffect = { kind: 'setVisibility', blockId: block.id, mode };
      options.push({ key: effectKey(effect), effect, text: t(CAPTURE_MODE_KEYS[mode]) });
    }
  }

  if (BYPASS_TYPES.has(block.type)) {
    const effect: FlowEffect = { kind: 'unlockBypass', blockId: block.id };
    options.push({ key: effectKey(effect), effect, text: t('blocks.captureMode.allowBypass') });
  }

  return options;
}

/** Which slots are spoken for, and by whom, so the menu can say which. */
export interface UsedEffectSlots {
  /** Held by the card the menu belongs to. */
  own: Set<string>;
  /** Held by any OTHER change in the task. */
  other: Set<string>;
}

/** Every slot a Changes list has spoken for, split into one card's own and everyone else's. */
export function usedEffectSlots(changes: { id: string; effects: FlowEffect[] }[], ownChangeId: string): UsedEffectSlots {
  const own = new Set<string>();
  const other = new Set<string>();
  for (const change of changes) {
    for (const effect of change.effects) {
      (change.id === ownChangeId ? own : other).add(effectSlot(effect));
    }
  }
  return { own, other };
}

/**
 * The whole menu, grouped by block, in task order.
 *
 * Exclusivity is PER CARD. Another change opening the same setting is not refused, and that is
 * deliberate: a card carries one match mode, so it can say `A or B` or `A and B` but never
 * `(A and B) or (C and D)`. Two cards are the only way to write that, and AND-shaped conditions
 * are strict enough that a row of them side by side is an ordinary way to author.
 *
 * Nothing can go wrong by allowing it, because the catalogue is monotonic: two changes opening
 * one setting can only agree, and applying an opening effect twice is a no-op by construction.
 * The ban existed to stop two cards disagreeing, which stopped being possible when the closing
 * directions were withdrawn. It also costs something real -- two cards can carry two NAMES, and
 * "Struggling with spelling" beside "Ran out of time" documents intent that one card's condition
 * soup does not.
 *
 * Within one card a repeat is still refused: two identical rows on the same card say nothing the
 * first does not. Greyed with a reason rather than dropped, the same as the condition menu --
 * an option that vanishes reads as a bug, one that is greyed answers the question.
 *
 * Slots, not keys, so a second visibility mode on one block is refused too.
 */
export function effectGroups(blocks: Block[], used: UsedEffectSlots, t: Translate): EffectGroup[] {
  return blocks
    .map((block, index) => ({
      blockId: block.id,
      blockLabel: getBlockLabel(block, index, t),
      options: effectOptionsFor(block, t).map((o) => {
        const slot = effectSlot(o.effect);
        return {
          ...o,
          disabled: used.own.has(slot) ? t('task.flow.changes.alreadyAdded') : o.disabled,
        };
      }),
    }))
    .filter((g) => g.options.length > 0);
}

/**
 * A stored effect as a sentence, for the effect row and the change's derived name.
 *
 * Reads the EFFECT, never the catalogue: an effect stays authored after the block's setting has
 * moved on, and at that point no menu row produces it any more. A label that could only be found
 * by looking it up in the menu would go blank exactly when the teacher needs to see what they
 * wrote.
 */
/**
 * An effect split into the two halves a one-line summary sets differently.
 *
 * The collapsed card bolds the block and leaves the action plain, exactly as a collapsed event
 * does with its conditions, so the two read as the same kind of sentence. `describeEffect` builds
 * the flat string from these, so the summary and the effect row can never disagree about wording.
 */
export function describeEffectParts(effect: FlowEffect, blocks: Block[], t: Translate): { block: string; action: string } {
  const index = blocks.findIndex((b) => b.id === effect.blockId);
  const block = blocks[index];
  const name = block ? getBlockLabel(block, index, t) : t('task.flow.rule.unknownBlock');

  const action = effect.kind === 'setVisibility'
    ? t(CAPTURE_MODE_KEYS[effect.mode])
    : effect.kind === 'setFlag'
      ? flagActionText(block ?? ({ data: {} } as Block), effect.field, effect.value, t)
      : t('blocks.captureMode.allowBypass');

  return { block: name, action };
}

export function describeEffect(effect: FlowEffect, blocks: Block[], t: Translate): string {
  const { block, action } = describeEffectParts(effect, blocks, t);

  // Capture mode gets its own sentence. The mode names are nouns the panel shows in a radio group
  // ("Capture on submit"), and "Capture on submit on GPS 1" is not a sentence — where a flag's
  // name is already a verb phrase and "Ignore accents on Keyword 1" is.
  if (effect.kind === 'setVisibility') {
    return t('task.flow.changes.effectCaptureMode', { block, mode: action });
  }
  return t('task.flow.changes.effectOn', { action, block });
}

/**
 * The block a Change has already acted on, as the student should now see it.
 *
 * Applied at render rather than written into `block.data`, which is the whole point: the stored
 * task is what the TEACHER authored and must not drift because a student took three goes at it.
 * The overlay is per-run state that happens to look like block data.
 *
 * Returns the SAME object when nothing applies. That is load-bearing twice over -- React skips the
 * re-render, and `use-restored-data` compares the incoming prop by value to decide whether someone
 * else changed the block, so handing it a fresh-but-equal object every render would have it
 * re-seeding the editor against an edit nobody made.
 */
export function applyEffectsToBlock(block: Block, effects: Record<string, FlowEffect>): Block {
  let data: Record<string, unknown> | null = null;
  const set = (key: string, value: unknown) => {
    data ??= { ...block.data };
    data[key] = value;
  };

  for (const effect of Object.values(effects)) {
    if (effect.blockId !== block.id) continue;
    // `bypassEnabled` is the stored field; `bypassAvailable` is what the block passes to evaluate
    // at submit time, derived from it. The effect opens the setting, not the derived reading.
    if (effect.kind === 'unlockBypass') set('bypassEnabled', true);
    else if (effect.kind === 'setFlag') set(effect.field, effect.value);
    else set('visibilityMode', effect.mode);
  }

  return data ? { ...block, data } : block;
}
